import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
import { Box, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { Button } from '@material-ui/core';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import useStyles from '../styles';
import PerFundAllocationStrategyCard from '../common/PerFundAllocationStrategyCard';
import { PlanContext } from '../../context/PlanContext';
import { formatCurrency } from '../../../../../shared/helpers/utils';
import { FundType, TierType } from '../../constants';
import FundPickerModal from '../common/funds/FundPickerModal';
import isTransactionComplete from '../../../../../shared/helpers/isTransactionComplete';
import {
  useFundStyles,
  AllocationLabelTypography,
  AllocationTypography,
  MessageTypography,
} from './styles';

// eslint-disable-next-line max-len
const errorMessage = 'Your total allocation from step 1 does not match the allocations set on this page. Either edit your allocations on this page or navigate to Step 1 to adjust your annual allocation.';
// eslint-disable-next-line max-len
const focusedFundMessage = 'We have suggested a new Focused Fund that may be of interest to you, or select "See Additional" to see our full Focused Fund roster.';
const FOLLOW_ON_FUND_FAMILY_CODES = [
  'av-cio-select-fund',
  'opportunity-fund',
];

const useScrollGreaterThan = (threshold) => {
  const [isGreater, setIsGreater] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const next = window.scrollY > threshold;
      setIsGreater((prev) => (prev === next ? prev : next));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  return isGreater;
};

const createModifiedFundData = (wordpressFundFamilies) => {
  if (Object.keys(wordpressFundFamilies).length === 0) {
    return wordpressFundFamilies;
  }

  return Object.fromEntries(
    Object.entries(wordpressFundFamilies).map(([key, value]) => {
      const modifiedValue = { ...value };
      if (modifiedValue.settings && modifiedValue.settings.fundTags) {
        modifiedValue.settings = {
          ...modifiedValue.settings,
          fundTags: modifiedValue.settings.fundTags.map((tag) => ({
            nsFundCode: tag?.fundTagFields?.nsFundCode,
          })),
        };
      }
      return [key, modifiedValue];
    }),
  );
};

const AllocationsHeader = ({
  fundClasses, remainingAllocation, getAllocation, type, error,
}) => {
  const headerShouldStick = useScrollGreaterThan(30);

  return (
    <Grid
      container
      xs={12}
      md={10}
      style={{
        position: headerShouldStick ? 'sticky' : 'relative', top: headerShouldStick ? '0' : 'auto', background: 'white', zIndex: 1,
      }}
    >
      <Grid item xs={12} md={6} className={fundClasses.allocationBox}>
        <Grid
          item
          xs={7}
          md={8}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <AllocationLabelTypography>
            Your Total Annual Allocation:
          </AllocationLabelTypography>
        </Grid>
        <Grid
          item
          xs={5}
          md={4}
          style={{ display: 'flex', justifyContent: 'flex-start' }}
        >
          <AllocationTypography
            className={fundClasses.allocationText}
            style={{ color: 'white', marginLeft: '10px' }}
          >
            {formatCurrency(getAllocation(type) || 0)}
          </AllocationTypography>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6} className={fundClasses.allocationBox}>
        <Grid
          item
          xs={7}
          md={8}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <AllocationLabelTypography>
            Allocation Remaining:
          </AllocationLabelTypography>
        </Grid>
        <Grid
          item
          xs={5}
          md={4}
          style={{ display: 'flex', justifyContent: 'flex-start' }}
        >
          <AllocationTypography
            className={`${fundClasses.allocationText}
      ${parseInt(remainingAllocation, 10) === 0 && !error ? 'disabled' : ''}
      ${parseInt(remainingAllocation, 10) > 0 || error ? 'error' : ''}
    `}
            style={{ color: 'white', marginLeft: '10px' }}
          >
            {formatCurrency(remainingAllocation)}
          </AllocationTypography>
        </Grid>
      </Grid>
    </Grid>
  );
};

AllocationsHeader.propTypes = {
  fundClasses: PropTypes.shape({
    allocationBox: PropTypes.string,
    allocationText: PropTypes.string,
  }).isRequired,
  remainingAllocation: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  getAllocation: PropTypes.func.isRequired,
  type: PropTypes.number.isRequired,
  error: PropTypes.bool.isRequired,
};

const FundPlan = ({ type, setNextDisabled }) => {
  const classes = useStyles();
  const fundClasses = useFundStyles();
  const {
    plan,
    updatedPlan,
    setUpdatedPlan,
    userPortfolio,
    fundFamilies,
    wordpressCoreFundData,
    wordpressFocusedFundData,
    planningConfig,
    loading,
  } = useContext(PlanContext);

  const planningConfigKeyByFundType = {
    [FundType.CORE]: 'coreFundsPlan',
    [FundType.FOCUSED]: 'focusedFundsPlan',
    [FundType.FOLLOW_ON]: 'followOnFundsPlan',
  };
  const planningConfigByFundType = planningConfig?.[
    planningConfigKeyByFundType[type]
  ];

  const [formData, setFormData] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [fundData, setFundData] = useState({});
  const [error, setError] = useState(false);
  const [showFocusedFundMessage, setShowFocusedFundMessage] = useState(false);
  const [remainingAllocation, setRemainingAllocation] = useState(0);
  const [typeInitialized, setTypeInitialized] = useState({});

  const getAllocation = (fundType) => {
    const fundTypeAllocation = plan?.fundTypeAllocations?.find(
      (candidateAllocation) => candidateAllocation.fundType === fundType,
    );
    return fundTypeAllocation?.amount && parseFloat(fundTypeAllocation.amount) > 0
      ? parseFloat(fundTypeAllocation.amount)
      : '';
  };

  const getRemainingAllocation = (updatedFormData, fundType) => {
    const fundTypeAllocation = updatedPlan?.fundTypeAllocations?.find(
      (candidateAllocation) => candidateAllocation.fundType === fundType,
    );
    if (!fundTypeAllocation) {
      return 0;
    }

    const totalAllocatedAmount = Object.values(updatedFormData).reduce(
      (total, fund) => total + (parseFloat(fund.amount) || 0),
      0,
    );
    const remainingAmount = parseInt(fundTypeAllocation.amount, 10)
      - totalAllocatedAmount;
    return remainingAmount;
  };

  function getVintageCodes(fundFamilyCode) {
    const fundFamily = fundFamilies.find(
      (family) => family.code === fundFamilyCode,
    );
    if (fundFamily) {
      return fundFamily.vintages.map((vintage) => vintage.code);
    }
    return [];
  }

  const getMatchingKeys = (filteredTransactions, modifiedFundFamilies) => {
    const matchingKeys = new Set();

    filteredTransactions.forEach((fund) => {
      if (fund.fundFamilyCode === 'TAF') {
        // Directly map 'TAF' to 'av-foundation-fund'
        matchingKeys.add('av-foundation-fund');
        return;
      }
      Object.keys(modifiedFundFamilies).forEach((key) => {
        const fundTags = modifiedFundFamilies[key].settings.fundTags || [];
        fundTags.forEach((tag) => {
          if (
            tag?.nsFundCode?.toLowerCase()
            === fund?.vintageFundCode?.toLowerCase()
          ) {
            matchingKeys.add(key);
          }
        });
      });
    });
    return matchingKeys;
  };

  const findMatchingKeyUsingfundVintages = (fundTags, modifiedFundFamilies) => {
    const matchingEntry = Object.entries(modifiedFundFamilies).find(([, value]) => {
      // Check if any fundTag in fundData matches the fundTags
      const fundTagCodes = (value.settings && value.settings.fundTags) || [];
      const fundTagCodesSet = new Set(
        fundTagCodes.map((tag) => tag.nsFundCode),
      );
      return fundTags.some((tag) => fundTagCodesSet.has(tag));
    });
    return matchingEntry?.[0] || null;
  };

  function selectDefaultAllocationFunds(modifiedFundFamilies) {
    if (type === FundType.FOLLOW_ON) {
      const followOnFunds = [];
      if (modifiedFundFamilies['av-cio-select-fund']) {
        followOnFunds.push('av-cio-select-fund');
      }
      if (
        userPortfolio.tier === TierType.DIAMOND
        && modifiedFundFamilies['opportunity-fund']
      ) {
        followOnFunds.push('opportunity-fund');
      }
      setSelectedFunds(followOnFunds);
      return;
    }

    const filteredTransactions = userPortfolio?.transactions?.length > 0
      ? userPortfolio.transactions.filter(
        (transaction) => isTransactionComplete(transaction)
              && transaction.vintageFundType !== 'FUNDRAISE'
              && ((type === FundType.CORE
                && ['AF', 'CF', 'FOUN', 'TAF'].includes(transaction.vintageFundType))
                || (type === FundType.FOCUSED
                  && transaction.vintageFundType === 'FF')),
      )
      : [];
    if (filteredTransactions && filteredTransactions.length > 0) {
      if (type === FundType.CORE) {
        const selectedFamilies = getMatchingKeys(filteredTransactions, modifiedFundFamilies) || [];
        setSelectedFunds(
          selectedFamilies.size > 0
            ? Array.from(selectedFamilies)
            : ['av-foundation-fund'],
        );
      } else {
        const closestOpenFundFamilyMap = planningConfig?.closestOpenFundFamilyMap
          ? Object.fromEntries(
            planningConfig.closestOpenFundFamilyMap.map(
              ({ closedFundFamily, openFundFamily }) => [closedFundFamily, openFundFamily],
            ),
          )
          : {};
        // For each transaction, retrieve the relevant WordPress fund family using the vintage.
        // if not found ,Retrieve the open fund family code from the map.
        // Collect all fund tags from the fund families.
        // Compare the nsFundCode from fundData with the fund tags; if any match, obtain the corresponding WordPress fund family key.
        // Fetch the WordPress fund family.
        const selectedFamilies = new Set();
        filteredTransactions.forEach((transaction) => {
          const matches = getMatchingKeys([transaction], modifiedFundFamilies);
          if (matches.size > 0) {
            matches.forEach((match) => selectedFamilies.add(match));
          } else {
            const closestOpenFundFamily = closestOpenFundFamilyMap[transaction.fundFamilyCode];
            if (closestOpenFundFamily) {
              const fundTags = getVintageCodes(closestOpenFundFamily);
              const matchingWordpressFundFamily = findMatchingKeyUsingfundVintages(
                fundTags,
                modifiedFundFamilies,
              );
              if (matchingWordpressFundFamily) {
                selectedFamilies.add(matchingWordpressFundFamily);
              }
            }
            setShowFocusedFundMessage(true);
          }
        });

        setSelectedFunds(Array.from(selectedFamilies));
      }
    } else {
      setSelectedFunds(
        type === FundType.CORE
          ? ['av-foundation-fund']
          : [],
      );
    }
  }

  useEffect(() => {
    const focusedFundData = Object.fromEntries(
      Object.entries(wordpressFocusedFundData).filter(
        ([key]) => !FOLLOW_ON_FUND_FAMILY_CODES.includes(key),
      ),
    );
    const allowedFollowOnFundFamilyCodes = userPortfolio.tier === TierType.DIAMOND
      ? FOLLOW_ON_FUND_FAMILY_CODES
      : ['av-cio-select-fund'];
    const followOnFundData = Object.fromEntries(
      Object.entries(wordpressFocusedFundData).filter(
        ([key]) => allowedFollowOnFundFamilyCodes.includes(key),
      ),
    );
    const wordpressFundDataByType = {
      [FundType.CORE]: wordpressCoreFundData,
      [FundType.FOCUSED]: focusedFundData,
      [FundType.FOLLOW_ON]: followOnFundData,
    };
    const modifiedFundFamilies = createModifiedFundData(
      wordpressFundDataByType[type],
    );
    setFundData(modifiedFundFamilies);

    if (
      !typeInitialized[type]
      && !loading
      && Array.isArray(fundFamilies)
      && fundFamilies.length > 0
      && Object.keys(plan).length !== 0
      && Object.keys(modifiedFundFamilies).length !== 0
    ) {
      const updatedFormData = plan.fundTypeAllocations
        .filter((fundTypeAllocation) => fundTypeAllocation.fundType === type)
        .reduce((acc, fundTypeAllocation) => {
          (fundTypeAllocation.perFundAllocations || []).forEach((perFundAllocation) => {
            if (
              !modifiedFundFamilies[perFundAllocation.fundFamilyCode]
              || parseFloat(perFundAllocation.amount) <= 0
            ) {
              return;
            }
            acc[perFundAllocation.fundFamilyCode] = {
              amount: perFundAllocation?.amount || 0,
              isAutoAnnualReinvestmentSelected:
                perFundAllocation?.isAutoAnnualReinvestmentSelected !== 0,
              isAutoDistributionReinvestmentSelected:
                perFundAllocation?.isAutoDistributionReinvestmentSelected !== 0,
            };
          });
          return acc;
        }, {});

      setFormData(updatedFormData);

      const fundCodes = Object.keys(updatedFormData);
      if (type === FundType.FOLLOW_ON) {
        setSelectedFunds(
          allowedFollowOnFundFamilyCodes.filter(
            (fundFamilyCode) => modifiedFundFamilies[fundFamilyCode],
          ),
        );
      } else if (fundCodes.length === 0) {
        selectDefaultAllocationFunds(modifiedFundFamilies);
      } else {
        setSelectedFunds(fundCodes);
      }

      let remainingAlloc = 0;
      const fundTypeAllocation = plan?.fundTypeAllocations?.find(
        (candidateAllocation) => candidateAllocation.fundType === type,
      );

      if (!fundTypeAllocation) {
        remainingAlloc = 0;
      } else {
        const totalAllocatedAmount = Object.values(updatedFormData).reduce(
          (sum, fundAllocation) => sum + parseFloat(fundAllocation.amount),
          0,
        );
        remainingAlloc = parseInt(fundTypeAllocation.amount, 10)
          - totalAllocatedAmount;
      }

      if (remainingAlloc < 0) {
        setError(true);
        setRemainingAllocation(0);
        setNextDisabled(true);
      } else {
        setError(false);
        setRemainingAllocation(remainingAlloc);
        setNextDisabled(remainingAlloc !== 0);
      }
      setTypeInitialized({ [type]: true });
    }
  }, [
    type,
    loading,
    plan,
    planningConfig,
    userPortfolio,
    wordpressCoreFundData,
    wordpressFocusedFundData,
    fundFamilies,
    typeInitialized,
  ]);

  const filterOutEmptyFunds = (allocationFormData) => Object.entries(
    allocationFormData,
  ).reduce((filtered, [fundFamilyCode, fundAllocation]) => (
    parseFloat(fundAllocation.amount) > 0
      ? { ...filtered, [fundFamilyCode]: fundAllocation }
      : filtered
  ), {});

  const updateExistingAllocations = (existingAllocations, updatedFormData) => {
    const updatedPerFundAllocations = [];
    const processedFundFamilyCodes = new Set();

    existingAllocations.forEach((existingAllocation) => {
      const { fundFamilyCode } = existingAllocation;

      if (updatedFormData[fundFamilyCode]) {
        updatedPerFundAllocations.push({
          ...existingAllocation,
          amount: (updatedFormData[fundFamilyCode].amount || 0).toString(),
          isAutoAnnualReinvestmentSelected:
            updatedFormData[fundFamilyCode]?.isAutoAnnualReinvestmentSelected ?? 1 ? 1 : 0,
          isAutoDistributionReinvestmentSelected:
            updatedFormData[fundFamilyCode]?.isAutoDistributionReinvestmentSelected ?? 1 ? 1 : 0,
        });
        processedFundFamilyCodes.add(fundFamilyCode);
      }
    });

    return { updatedPerFundAllocations, processedFundFamilyCodes };
  };

  const addNewAllocations = (
    filteredFormData,
    updatedPerFundAllocations,
    processedFundFamilyCodes,
    allocationId,
  ) => {
    Object.keys(filteredFormData).forEach((fundFamilyCode) => {
      if (!processedFundFamilyCodes.has(fundFamilyCode)) {
        updatedPerFundAllocations.push({
          investmentPlanFundTypeAllocationId: allocationId,
          fundFamilyCode,
          amount: (filteredFormData[fundFamilyCode].amount || 0).toString(),
          isAutoAnnualReinvestmentSelected:
            filteredFormData[fundFamilyCode]?.isAutoAnnualReinvestmentSelected ?? 1 ? 1 : 0,
          isAutoDistributionReinvestmentSelected:
            filteredFormData[fundFamilyCode]?.isAutoDistributionReinvestmentSelected ?? 1 ? 1 : 0,
        });
      }
    });

    return updatedPerFundAllocations;
  };

  const updatePlanWithAllocations = (planToUpdate, updatedAllocations) => ({
    ...planToUpdate,
    fundTypeAllocations: updatedAllocations,
  });

  const handleUpdateAllocation = useCallback(
    (updatedFormData) => {
      if (!loading) {
        const filteredFormData = filterOutEmptyFunds(updatedFormData);
        const updatedAllocations = [...updatedPlan.fundTypeAllocations];
        const fundIndex = updatedAllocations.findIndex(
          (allocation) => allocation.fundType === type,
        );

        if (fundIndex !== -1) {
          const existingPerFundAllocations = updatedAllocations[fundIndex].perFundAllocations;
          const { updatedPerFundAllocations, processedFundFamilyCodes } = updateExistingAllocations(
            existingPerFundAllocations,
            filteredFormData,
          );

          const finalPerFundAllocations = addNewAllocations(
            filteredFormData,
            updatedPerFundAllocations,
            processedFundFamilyCodes,
            updatedAllocations[fundIndex].id,
          );

          updatedAllocations[fundIndex].perFundAllocations = finalPerFundAllocations;
          setUpdatedPlan(
            updatePlanWithAllocations(updatedPlan, updatedAllocations),
          );
        }
      }

      const remainingAlloc = getRemainingAllocation(updatedFormData, type);
      if (remainingAlloc < 0) {
        setError(true);
        setRemainingAllocation(0);
        setNextDisabled(true);
      } else {
        setError(false);
        setRemainingAllocation(remainingAlloc);
        setNextDisabled(
          remainingAlloc !== 0,
        );
      }
    },
    [formData, updatedPlan, setUpdatedPlan, setNextDisabled, type, loading],
  );

  if (!typeInitialized[type] || !planningConfigByFundType) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box className={classes.content}>
      <Typography
        variant="h1ExtraLarge"
        component="h1"
        style={{ color: '#191919' }}
      >
        {planningConfigByFundType?.title}
      </Typography>
      {[FundType.FOCUSED, FundType.FOLLOW_ON].includes(type) && (
        <Typography
          variant="body2Inter"
          style={{ color: '#797979', padding: 15 }}
          dangerouslySetInnerHTML={{
            __html: planningConfigByFundType?.subtitle?.richtext || '',
          }}
        />
      )}

      <AllocationsHeader
        fundClasses={fundClasses}
        remainingAllocation={remainingAllocation}
        getAllocation={getAllocation}
        type={type}
        error={error}
      />
      {type === FundType.FOCUSED && showFocusedFundMessage && (
        <MessageTypography style={{ marginTop: '16px' }}>
          {focusedFundMessage}
        </MessageTypography>
      )}
      {error && (
        <MessageTypography style={{ marginTop: '16px' }}>
          {errorMessage}
        </MessageTypography>
      )}
      <Grid container xs={12} sm={10}>
        {Array.from(selectedFunds).map((fundKey) => {
          const fund = fundData[fundKey];
          return (
            <Grid item xs={12} key={fundKey}>
              <PerFundAllocationStrategyCard
                logo={fund?.fields?.logo?.url}
                title={fund?.fields?.fundFamilyDisplayName || 'Fund Title'}
                fundFamilysubtitle={(type === FundType.CORE ? fund?.general?.title : fund?.fields?.subTitle) || ''}
                subtitle={
                  planningConfigByFundType?.fundDescriptiveText?.richtext || ''
                }
                autoAnnualReinvestmentLabel={planningConfigByFundType?.autoAnnualReinvestmentSelectionLabel}
                autoDistributionReinvestmentLabel={planningConfigByFundType?.autoDistributionReinvestmentSelectionLabel}
                formData={formData}
                setFormData={setFormData}
                fund={fundKey}
                onUpdateAllocation={handleUpdateAllocation}
                fundData={fundData}
                fundFamilies={fundFamilies}
              />
            </Grid>
          );
        })}
        {type !== FundType.FOLLOW_ON && (
          <>
            <Button
              color="secondary"
              variant="contained"
              style={{
                marginBottom: '10px',
                background: '#CFB72E',
                alignSelf: 'flex-start',
              }}
              onClick={() => setOpenModal(true)}
            >
              See Additional {type === FundType.CORE ? 'Core' : 'Focused'} Funds
            </Button>
            <FundPickerModal
              open={openModal}
              onClose={() => setOpenModal(false)}
              fundData={fundData}
              selectedFunds={selectedFunds}
              setSelectedFunds={setSelectedFunds}
              setFormData={setFormData}
              onUpdateAllocation={handleUpdateAllocation}
              modalTitle={planningConfigByFundType?.fundPickerModalTitle}
              fundType={type}
            />
          </>
        )}
      </Grid>
    </Box>
  );
};

FundPlan.propTypes = {
  type: PropTypes.number.isRequired,
  setNextDisabled: PropTypes.func.isRequired,
};

export default FundPlan;
